import { TreeKeyManagerItem } from '@angular/cdk/a11y';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { StateService } from '@uirouter/core';

import { CompanyTreeRow } from 'src/app/app-common/types/company-tree';
import { ComponentsModule } from 'src/app/components/components.module';

const INDENT_PER_LEVEL_PX = 32;

@Component({
  selector: 'div[company-tree-row]',
  templateUrl: './company-tree-row.component.html',
  styleUrl: './company-tree-row.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [ComponentsModule, MatIconModule],
  hostDirectives: [MatTooltip],
  host: {
    class: 'company-tree-row',
    '[class.company-tree-row--selected]': 'showSelectedStyle()',
    '[attr.role]': 'ariaRole()',
    '[attr.tabindex]': 'tabIndex()',
    '[attr.aria-level]': 'treeAriaLevel()',
    '[attr.aria-posinset]': 'treeAriaPosInSet()',
    '[attr.aria-setsize]': 'treeAriaSetSize()',
    '[attr.aria-expanded]': 'ariaExpanded()',
    '[attr.aria-current]': 'ariaCurrent()',
    '[attr.aria-label]': 'accessibleName()',
    '(click)': 'onClick($event)',
    '(keydown)': 'onKeydown($event)',
  },
})
export class CompanyTreeRowComponent implements TreeKeyManagerItem {
  private readonly stateService = inject(StateService);
  private readonly hostEl = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly tooltip = inject(MatTooltip);

  readonly row = input.required<CompanyTreeRow>();

  readonly selectCompany = output<string>();
  readonly toggleExpand = output<string>();
  readonly retry = output<string>();

  private readonly focusableState = signal(false);
  private parentRow: CompanyTreeRowComponent | null = null;
  private childRows: CompanyTreeRowComponent[] = [];

  protected readonly isFocusable = this.focusableState.asReadonly();

  protected readonly isSearch = computed(() => this.row().variant === 'search');

  protected readonly ariaRole = computed(() =>
    this.isSearch() ? 'listitem' : 'treeitem',
  );

  protected readonly tabIndex = computed(() => {
    if (this.isSearch()) {
      return 0;
    }

    return this.isFocusable() ? 0 : -1;
  });

  protected readonly treeAriaLevel = computed(() =>
    this.isSearch() ? null : this.row().level,
  );

  protected readonly treeAriaPosInSet = computed(() =>
    this.isSearch() ? null : this.row().posInSet,
  );

  protected readonly treeAriaSetSize = computed(() =>
    this.isSearch() ? null : this.row().setSize,
  );

  protected readonly ariaCurrent = computed(() =>
    !this.isSearch() && this.row().selected ? 'true' : null,
  );

  protected readonly showSelectedStyle = computed(
    () => !this.isSearch() && this.row().selected,
  );

  protected readonly displayCountLabel = computed(() => {
    const count = this.row().displayCount;
    if (count === undefined) {
      return null;
    }

    if (count === 0) {
      return 'No displays';
    }

    return `${count} display${count === 1 ? '' : 's'}`;
  });

  protected readonly parentLine = computed(() => {
    if (!this.isSearch()) {
      return null;
    }

    const parentName = this.row().parentName?.trim();
    return parentName ? `in ${parentName}` : null;
  });

  protected readonly showExpandControl = computed(() => {
    const row = this.row();
    return row.variant === 'branch' && row.expandable;
  });

  /**
   * Every level below the root reserves one indentation step, and the row's own
   * expand control sits in the last of them. Rows without a control pad that
   * step out instead, so a leaf stays aligned with its expandable siblings.
   */
  protected readonly indentWidth = computed(() => {
    if (this.isSearch()) {
      return 0;
    }

    const steps = Math.max(this.row().level - 1, 0);
    const control = this.showExpandControl() ? 1 : 0;
    return Math.max(steps - control, 0) * INDENT_PER_LEVEL_PX;
  });

  protected readonly showError = computed(() => !!this.row().errorMessage);

  protected readonly showSpinner = computed(
    () => this.row().loading && !this.showError(),
  );

  protected readonly showCheck = computed(
    () =>
      !this.isSearch() &&
      this.row().selected &&
      !this.showError() &&
      !this.showSpinner(),
  );

  protected readonly showDot = computed(() => {
    const row = this.row();
    return (
      !this.isSearch() &&
      row.containsSelected &&
      !row.expanded &&
      !row.selected &&
      !this.showError() &&
      !this.showSpinner()
    );
  });

  protected readonly fullText = computed(() => {
    const parts = [];
    for (const part of [this.displayCountLabel()]) {
      if (part) {
        parts.push(part);
      }
    }

    return parts.join(', ');
  });

  constructor() {
    effect(() => {
      const message = this.fullText();
      untracked(() => {
        this.tooltip.message = message;
        this.tooltip.disabled = false;
      });
    });
  }

  protected href(): string {
    const companyId = this.row().id;
    const current = this.stateService.current?.name;

    return current
      ? this.stateService.href(current, {
          ...this.stateService.params,
          cid: companyId,
        })
      : this.stateService.href('apps.home', { cid: companyId });
  }

  /**
   * A non-empty `aria-label` replaces the row's descendant text, so the label
   * has to carry everything the row shows — name, address and parent line —
   * before the count and the row state. The tooltip stays count-only.
   */
  protected accessibleName(): string {
    const row = this.row();
    const parts = [];

    for (const part of [
      row.name?.trim(),
      row.address,
      this.parentLine(),
      this.displayCountLabel(),
    ]) {
      if (part) {
        parts.push(part);
      }
    }

    if (row.selected) {
      parts.push('selected');
    }

    if (this.showDot()) {
      parts.push('contains selected company');
    }

    if (this.showError()) {
      parts.push('failed to load, press Alt+Enter to retry');
    }

    return parts.join(', ');
  }

  protected ariaExpanded(): boolean | null {
    const row = this.row();
    if (row.variant === 'search') {
      return null;
    }

    if (row.variant === 'root') {
      return true;
    }

    return row.expandable ? row.expanded : null;
  }

  protected onClick(event: MouseEvent): void {
    if (
      event.button !== 0 ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    event.preventDefault();
    this.selectCompany.emit(this.row().id);
  }

  protected onToggle(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.toggleExpand.emit(this.row().id);
  }

  setTreeLinks(
    parent: CompanyTreeRowComponent | null,
    children: CompanyTreeRowComponent[],
  ): void {
    this.parentRow = parent;
    this.childRows = children;
  }

  getParent(): TreeKeyManagerItem | null {
    return this.parentRow;
  }

  getChildren(): TreeKeyManagerItem[] {
    return this.childRows;
  }

  isExpanded = (): boolean =>
    this.row().variant === 'root' || this.row().expanded;

  getLabel(): string {
    return this.row().name;
  }

  activate(): void {
    this.selectCompany.emit(this.row().id);
  }

  expand(): void {
    const row = this.row();
    if (row.variant === 'root' || !row.expandable || row.expanded) {
      return;
    }

    this.toggleExpand.emit(row.id);
  }

  collapse(): void {
    const row = this.row();
    if (row.variant === 'root' || !row.expanded) {
      return;
    }

    this.toggleExpand.emit(row.id);
  }

  focus(): void {
    this.focusableState.set(true);
    const element = this.hostEl.nativeElement;
    const tree = element.closest('[role="tree"]');
    const active = element.ownerDocument.activeElement;
    if (tree && active && tree.contains(active)) {
      element.focus();
    }
  }

  unfocus(): void {
    this.focusableState.set(false);
  }

  makeFocusable(): void {
    this.focusableState.set(true);
  }

  protected onRetry(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.retry.emit(this.row().id);
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (this.showError() && event.key === 'Enter' && event.altKey) {
      event.preventDefault();
      event.stopPropagation();
      this.retry.emit(this.row().id);
      return;
    }

    // Tree rows are activated by TreeKeyManager; search rows have no key
    // manager, so the row handles its own activation keys.
    if (!this.isSearch() || event.altKey || event.ctrlKey || event.metaKey) {
      return;
    }

    if (
      event.key === 'Enter' ||
      event.key === ' ' ||
      event.key === 'Spacebar'
    ) {
      event.preventDefault();
      this.selectCompany.emit(this.row().id);
    }
  }
}
